/**
 * Mathematical utilities for squircle (continuous corner) calculations.
 * These functions ensure consistency between SVG and Canvas implementations.
 * 
 * CONVERGENCE PREVENTION SOLUTION:
 * ================================
 * 
 * This implementation solves the fundamental mathematical convergence problem that occurs
 * when the effective radius approaches or equals the full extent of the coordinate system.
 * 
 * The Mathematical Problem:
 * -------------------------
 * In the normalized coordinate system [-1, 1], squircle corner generation uses:
 * - smoothedRadius = normalizedRadius * smoothing
 * - effectiveRadius = smoothedRadius * (continuous ? 2 : 1)
 * 
 * When either value reaches 1.0, the corner generation formulas break down because:
 * 1. cornerInset = smoothedRadius - sin(π/4) * smoothedRadius approaches the edge boundary
 * 2. endInset = min(1, effectiveRadius) becomes exactly 1, causing edge points to converge
 * 3. The control point calculations become degenerate, leading to collapsed curves
 * 
 * Specific Convergence Cases:
 * ---------------------------
 * - When radius = 50 and dimension = 100: normalizedRadius = 1.0
 * - With smoothing = 0.6: smoothedRadius = 0.6, effectiveRadius = 1.2 (exceeds boundary!)
 * - With smoothing = 1.0: smoothedRadius = 1.0 (reaches boundary!)
 * - Result: P0/P6 points collapse to identical positions, destroying curve geometry
 * 
 * The Convergence Prevention Fix:
 * -------------------------------
 * Lines 22-28 implement strict boundary enforcement:
 * 
 * if (smoothedRadius >= 1) {
 *   smoothedRadius = 0.999999;    // Ensures corner formulas remain well-defined
 *   effectiveRadius = 0.4999999;  // Prevents endInset from reaching 1.0
 * } else if (effectiveRadius >= 1) {
 *   effectiveRadius = 0.999999;   // Clamps effective radius below boundary
 *   smoothedRadius = 0.4999999;   // Maintains mathematical consistency
 * }
 * 
 * Why This Works:
 * ---------------
 * 1. Maintains mathematical validity: All formulas remain well-defined
 * 2. Preserves curve continuity: Corner points stay slightly inset from boundaries
 * 3. Prevents coordinate collapse: P0/P6 points maintain proper spacing
 * 4. Handles extreme aspect ratios: Works for any rectangle dimensions
 * 5. Invisible to users: 0.999999 vs 1.0 is imperceptible visually
 * 
 * The key insight is that perfect mathematical radius limits (radius = dimension/2)
 * cause degenerate geometry, so we must enforce strict inequalities rather than
 * allowing equality conditions that break the underlying mathematics.
 * 
 * DETAILED MATHEMATICAL ANALYSIS:
 * ===============================
 * 
 * The squircle corner generation relies on these core formulas:
 * 
 * 1. cornerInset = smoothedRadius - sin(π/4) * smoothedRadius
 *                = smoothedRadius * (1 - √2/2)
 *                = smoothedRadius * (1 - 0.7071...)
 *                = smoothedRadius * 0.2928...
 * 
 * 2. endInset = min(1, effectiveRadius)
 * 
 * 3. P0 = [-1, -1 + endInset]  // Start point on left edge
 * 
 * 4. P3 = [-1 + cornerInset, -1 + cornerInset]  // Corner endpoint
 * 
 * Convergence Breakdown Analysis:
 * -------------------------------
 * 
 * Case 1: When smoothedRadius = 1.0 (Tangent Length Singularity)
 * - cornerInset = 1.0 * 0.2928 = 0.2928
 * - P3 = [-1 + 0.2928, -1 + 0.2928] = [-0.7072, -0.7072]
 * - tanLength = (4/3) * tan(π/16) * smoothedRadius = 0.1989 * 1.0 = 0.1989
 * 
 * The tangent breakdown occurs because:
 * tanLength = (4/3) * tan(π/16) * smoothedRadius
 * 
 * When smoothedRadius = 1.0, we get the maximum possible tangent length in the
 * normalized [-1,1] coordinate system. The factor (4/3) * tan(π/16) ≈ 0.1989
 * represents the optimal Bézier control point distance for a quarter-circle arc.
 * 
 * At smoothedRadius = 1.0:
 * - The corner tries to occupy the entire coordinate space
 * - Control points P1 and P2 become positioned at the geometric limits
 * - P1 = [-1, -1 + endInset - sideLength] pushes toward the boundary
 * - P2 calculation involves cornerLength * sin(π/4) which amplifies the effect
 * - The Bézier curve becomes "stretched" to its mathematical breaking point
 * 
 * Mathematical Instability at the Boundary:
 * When smoothedRadius approaches 1.0, the control points approach positions where:
 * - They may exceed the coordinate bounds [-1, 1]
 * - The curve curvature becomes extreme (approaching infinity)
 * - Small numerical errors get amplified exponentially
 * - The smooth continuous corner property is lost
 * 
 * DEEP DIVE: Control Point Formula Breakdown at smoothedRadius = 1.0
 * ==================================================================
 * 
 * The control points are calculated as:
 * P1 = [-1, -1 + endInset - sideLength]
 * P2 = [-1 + cornerInset - cornerLength * sin(π/4), -1 + cornerInset + cornerLength * sin(π/4)]
 * 
 * Where for continuous corners:
 * cornerLength = (cornerInset / sin(π/4)) * (1 - overshoot) + tanLength * overshoot
 * sideLength = (endInset - endInset * sin(π/4) + tanLength) * (1 - overshoot) + tanLength * overshoot
 * 
 * At smoothedRadius = 1.0 with effectiveRadius = 2.0 (continuous mode):
 * - endInset = min(1, 2.0) = 1.0
 * - overshoot = effectiveRadius - endInset = 2.0 - 1.0 = 1.0
 * - cornerLength = 0 * (1 - 1.0) + 0.1989 * 1.0 = 0.1989
 * - sideLength = (1.0 - 0.7071 + 0.1989) * 0 + 0.1989 * 1.0 = 0.1989
 * 
 * This produces:
 * P1 = [-1, -1 + 1.0 - 0.1989] = [-1, -0.1989]
 * P2 = [-1 + 0.2928 - 0.1989 * 0.7071, -1 + 0.2928 + 0.1989 * 0.7071]
 *    = [-1 + 0.2928 - 0.1406, -1 + 0.2928 + 0.1406]  
 *    = [-0.8478, -0.5666]
 * 
 * The Catastrophic Singularity:
 * -----------------------------
 * Notice that when overshoot = 1.0 (which happens when effectiveRadius ≥ 1.0):
 * - The first term of both cornerLength and sideLength formulas becomes ZERO
 * - The entire calculation reduces to just tanLength
 * - All the sophisticated continuous corner blending is lost
 * - The curve degenerates to a basic circular arc approximation
 * 
 * This is why both smoothedRadius AND effectiveRadius must stay below 1.0:
 * The overshoot calculation creates a mathematical discontinuity that destroys
 * the smooth interpolation between corner styles that makes squircles work.
 * 
 * Case 2: When effectiveRadius ≥ 1.0 (the critical failure)
 * - endInset = min(1, effectiveRadius) = 1.0
 * - P0 = [-1, -1 + 1] = [-1, 0]  ← P0 moves to Y=0 line!
 * 
 * The Geometric Catastrophe:
 * --------------------------
 * When endInset = 1.0, the start point P0 jumps from the bottom edge region
 * to the center horizontal line (Y=0). This creates several problems:
 * 
 * 1. **Loss of Edge Continuity**: P0 is no longer near the rectangle edge
 * 2. **Control Point Divergence**: The tangent control points become extreme
 * 3. **Bézier Curve Collapse**: The curve loses its corner-hugging property
 * 4. **Asymptotic Behavior**: Small changes in radius cause large jumps in geometry
 * 
 * The Mathematical Singularity:
 * -----------------------------
 * The root cause is that when effectiveRadius = 1.0:
 * 
 * endInset = 1.0
 * ⟹ P0.y = -1 + 1 = 0
 * ⟹ The "corner" start point is at the rectangle's center line
 * ⟹ The Bézier curve must span from center to actual corner
 * ⟹ Control point calculations become ill-conditioned
 * ⟹ Numerical instability and visual artifacts result
 * 
 * The fix ensures endInset < 1.0 always, keeping P0 properly positioned
 * near the rectangle boundary where the corner mathematics remain stable.
 */

import { SquircleMathTypes } from "./squircle-math-types";

export namespace SquircleMath {
  export type Point = SquircleMathTypes.Point;
  export type CalcParams = SquircleMathTypes.CalcParams;
  export type CalcResult = SquircleMathTypes.CalcResult;

  /**
   * Calculate intermediate values for squircle mathematics.
   */
  export function calcParams(params: CalcParams): CalcResult {
    const { normalizedRadius, smoothing, continuous } = params;

    // Apply smoothing to the radius first, then use original formulas
    let smoothedRadius = normalizedRadius * smoothing;
    let effectiveRadius = smoothedRadius * (continuous ? 2 : 1);
    if (smoothedRadius >= 1) {
      smoothedRadius = 0.999999;
      effectiveRadius = 0.4999999;
    } else if (effectiveRadius >= 1) {
      effectiveRadius = 0.999999;
      smoothedRadius = 0.4999999;
    }

    const tanLength = (4 / 3) * Math.tan(Math.PI / 16) * smoothedRadius;

    // Use original formula without extra smoothing multiplication
    const cornerInset = smoothedRadius - Math.sin(Math.PI / 4) * smoothedRadius;
    const endInset = Math.min(1, effectiveRadius);
    const overshoot = effectiveRadius - endInset;

    const cornerLength = continuous
      ? (cornerInset / Math.sin(Math.PI / 4)) * (1 - overshoot) +
        tanLength * overshoot
      : tanLength;
    const sideLength = continuous
      ? (endInset - endInset * Math.sin(Math.PI / 4) + tanLength) *
          (1 - overshoot) +
        tanLength * overshoot
      : tanLength;

    return {
      effectiveRadius,
      tanLength,
      cornerInset,
      endInset,
      overshoot,
      cornerLength,
      sideLength,
    };
  }

  /**
   * Generate the 4 control points for a corner in normalized coordinates.
   * Returns points in the coordinate system [-1, 1].
   */
  export function genCornerPoints(
    params: SquircleMathTypes.CalcResult,
  ): Point[] {
    const { cornerInset, endInset, cornerLength, sideLength } = params;

    return [
      [-1, -1 + endInset], // P0: Start point on edge
      [-1, -1 + endInset - sideLength], // P1: Control point 1
      [
        -1 + cornerInset - cornerLength * Math.sin(Math.PI / 4),
        -1 + cornerInset + cornerLength * Math.sin(Math.PI / 4),
      ], // P2: Control point 2
      [-1 + cornerInset, -1 + cornerInset], // P3: End point
    ];
  }

  /**
   * Convert normalized corner points to pixel coordinates.
   * Maps from [-1, 1] coordinate system to [0, size] pixel coordinates.
   */
  export function toPixels(
    normalizedPoints: Point[],
    pixelSize: number,
  ): Point[] {
    return normalizedPoints.map(([x, y]) => [
      ((x + 1) * pixelSize) / 2, // x: [-1,1] -> [0,pixelSize]
      ((y + 1) * pixelSize) / 2, // y: [-1,1] -> [0,pixelSize]
    ]);
  }

  /**
   * Complete squircle calculation pipeline.
   * Takes input parameters and returns pixel-space control points.
   */
  export function calcControlPoints(
    normalizedRadius: number,
    smoothing: number,
    pixelSize: number,
    continuous: boolean = true,
  ): Point[] {
    const params = calcParams({
      normalizedRadius,
      smoothing,
      continuous,
    });

    const normalizedPoints = genCornerPoints(params);
    return toPixels(normalizedPoints, pixelSize);
  }

  // Helper functions
  export const rotate = ([x, y]: Point, θ: number): Point => {
    return [
      x * Math.cos(θ) + y * Math.sin(θ),
      -x * Math.sin(θ) + y * Math.cos(θ),
    ];
  };

  export const rotatePoints = (points: Point[], rotation: number) =>
    points.map((p) => rotate(p, Math.PI * rotation));

  export const joinPoints = (points: Point[]) =>
    points.map((coord) => coord.join(" ")).join(" ");

  /**
   * Transform points using the 3-step algorithm:
   * 1. Points generated using shorter axis (keeping corner shape unchanged)
   * 2. ONLY translate corner positions to rectangle corners
   * 3. Add extra length in longer dimension without affecting corners
   */
  export const scaleForAspect = (
    aspectRatio: number,
    points: Point[],
  ): Point[] => {
    if (aspectRatio === 1) {
      return points; // Square: no transformation needed
    }

    return points.map(([x, y]) => {
      if (aspectRatio > 1) {
        // Wide rectangle: translate X coordinates to new boundary positions
        // Map edge points: -1 → -aspectRatio, +1 → +aspectRatio
        // But preserve the internal corner curvature proportionally
        let translatedX: number;
        if (Math.abs(x) === 1) {
          // Edge points: translate to new boundaries
          translatedX = x * aspectRatio;
        } else {
          // Internal corner points: keep original proportions but offset to new edge
          const edgeDistance = 1 - Math.abs(x); // Distance from edge in original
          const newEdge = aspectRatio;
          translatedX =
            x < 0 ? -(newEdge - edgeDistance) : newEdge - edgeDistance;
        }
        return [translatedX, y] as Point;
      } else {
        // Tall rectangle: same logic for Y axis
        let translatedY: number;
        if (Math.abs(y) === 1) {
          translatedY = y / aspectRatio;
        } else {
          const edgeDistance = 1 - Math.abs(y);
          const newEdge = 1 / aspectRatio;
          translatedY =
            y < 0 ? -(newEdge - edgeDistance) : newEdge - edgeDistance;
        }
        return [x, translatedY] as Point;
      }
    });
  };

  export const genCorners = (aspectRatio: number, corner: Point[]) =>
    [0, -0.5, -1, -1.5].map((rotation) =>
      [
        corner.slice(0, 1),
        corner.slice(1, 4),
        corner
          .slice(0, 3)
          .map((p) => [...p].reverse() as Point)
          .reverse(),
      ]
        .map((points) => rotatePoints(points, rotation))
        .map((points) => scaleForAspect(aspectRatio, points)),
    );

  // Bezier curve calculations
  export const bezierPos = (t: number, ...points: Point[]): Point => {
    const f = (c0: number, c1: number, c2: number, c3: number) =>
      c0 * (1 - t) ** 3 +
      c1 * 3 * (1 - t) ** 2 * t +
      c2 * 3 * (1 - t) * t ** 2 +
      c3 * t ** 3;
    const xs = points.map((p) => p[0]);
    const ys = points.map((p) => p[1]);
    return [f(xs[0], xs[1], xs[2], xs[3]), f(ys[0], ys[1], ys[2], ys[3])];
  };

  export const bezierCurve = (
    t: number,
    ...points: Point[]
  ): [number, Point] => {
    const d1f = (c0: number, c1: number, c2: number, c3: number) =>
      (c1 - c0) * 3 * (1 - t) ** 2 +
      (c2 - c1) * 6 * (1 - t) * t +
      (c3 - c2) * 3 * t ** 2;
    const d2f = (c0: number, c1: number, c2: number, c3: number) =>
      (c2 - c1 * 2 + c0) * 6 * (1 - t) + (c3 - c2 * 2 + c1) * 6 * t;
    const xs = points.map((p) => p[0]);
    const ys = points.map((p) => p[1]);
    const d1 = [
      d1f(xs[0], xs[1], xs[2], xs[3]),
      d1f(ys[0], ys[1], ys[2], ys[3]),
    ];
    const d2 = [
      d2f(xs[0], xs[1], xs[2], xs[3]),
      d2f(ys[0], ys[1], ys[2], ys[3]),
    ];
    const curvature =
      (d1[0] * d2[1] - d1[1] * d2[0]) / (d1[0] ** 2 + d1[1] ** 2) ** (3 / 2);
    const normal = [-d1[1], d1[0]];
    const mag = Math.sqrt(normal[0] ** 2 + normal[1] ** 2);
    return [
      curvature,
      normal.map((c) => ((c * 1) / mag) * curvature * -0.1) as Point,
    ];
  };

  export function genSvg(
    size: Size,
    radius: number,
    smoothing: number,
  ): {
    viewBox: string;
    pathData: string;
    cornerParams: CalcResult;
    corners: Point[][][];
  } {
    // Calculate aspect ratio
    const aspectRatio = size.width! / size.height!;

    // Step 1: Generate points using shorter axis dimension
    // Always use the shorter dimension for radius normalization
    const minDimension = Math.min(size.width!, size.height!);
    const maxPossibleRadius = minDimension / 2;
    const clampedRadius = Math.min(radius, maxPossibleRadius);

    // Normalize radius based on shorter axis (this creates a square base)
    const normalizedRadius = clampedRadius / maxPossibleRadius;

    const continuous = smoothing > 0;

    // Generate corner parameters using shorter axis
    const cornerParams = calcParams({
      normalizedRadius: normalizedRadius,
      smoothing: smoothing,
      continuous: continuous,
    });

    // Generate base corner points (in square coordinate system)
    const corner = genCornerPoints(cornerParams);

    // Step 4: Create appropriate viewBox for the scaled coordinates
    let viewBox: string;

    if (aspectRatio > 1) {
      // Wide rectangle: X scaled to aspectRatio, Y stays at 1
      viewBox = `${-aspectRatio} -1 ${2 * aspectRatio} 2`;
    } else {
      // Tall rectangle: Y scaled to 1/aspectRatio, X stays at 1
      const inverseAR = 1 / aspectRatio;
      viewBox = `-1 ${-inverseAR} 2 ${2 * inverseAR}`;
    }

    const corners = genCorners(aspectRatio, corner);

    // Calculate the starting point to match the scaled coordinates
    let startX: number;
    let startY: number;

    if (aspectRatio > 1) {
      // Wide rectangle: X scaled, Y unscaled
      startX = -aspectRatio;
      startY = 1 - cornerParams.endInset;
    } else {
      // Tall rectangle: Y scaled, X unscaled
      startX = -1;
      startY = (1 - cornerParams.endInset) / aspectRatio;
    }
    const pathData = `
      M ${startX} ${startY}
      ${corners
        .map(
          (c) => `
        L ${joinPoints(c[0])}
        C ${joinPoints(c[1])}
        C ${joinPoints(c[2])}
      `,
        )
        .join("")}
    Z`;
    return { viewBox, pathData, cornerParams, corners };
  }

  /**
   * Generate path data in pixel coordinates for CSS clip-path: path()
   */
  export function genPixelPath(
    size: Size,
    radius: number,
    smoothing: number,
  ): string {
    const width = size.width!;
    const height = size.height!;
    const aspectRatio = width / height;

    const minDimension = Math.min(width, height);
    const maxPossibleRadius = minDimension / 2;
    const clampedRadius = Math.min(radius, maxPossibleRadius);
    const normalizedRadius = clampedRadius / maxPossibleRadius;

    const continuous = smoothing > 0;
    const cornerParams = calcParams({
      normalizedRadius,
      smoothing,
      continuous,
    });

    const corner = genCornerPoints(cornerParams);
    const corners = genCorners(aspectRatio, corner);

    // Transform from normalized coordinates to pixel coordinates
    const toPixel = (p: Point): Point => {
      if (aspectRatio > 1) {
        // Wide: x in [-aspectRatio, aspectRatio], y in [-1, 1]
        return [
          ((p[0] / aspectRatio + 1) / 2) * width,
          ((p[1] + 1) / 2) * height,
        ];
      } else {
        // Tall: x in [-1, 1], y in [-1/ar, 1/ar]
        const inverseAR = 1 / aspectRatio;
        return [
          ((p[0] + 1) / 2) * width,
          ((p[1] * aspectRatio + 1) / 2) * height,
        ];
      }
    };

    // Calculate starting point in pixels
    let startX: number;
    let startY: number;
    if (aspectRatio > 1) {
      startX = 0;
      startY = ((1 - cornerParams.endInset + 1) / 2) * height;
    } else {
      startX = 0;
      startY = (((1 - cornerParams.endInset) / aspectRatio + 1 / aspectRatio) / (2 / aspectRatio)) * height;
    }

    const pathParts: string[] = [`M ${startX} ${startY}`];

    for (const c of corners) {
      const l = toPixel(c[0][0]);
      pathParts.push(`L ${l[0]} ${l[1]}`);

      const c1 = c[1].map(toPixel);
      pathParts.push(`C ${c1[0][0]} ${c1[0][1]} ${c1[1][0]} ${c1[1][1]} ${c1[2][0]} ${c1[2][1]}`);

      const c2 = c[2].map(toPixel);
      pathParts.push(`C ${c2[0][0]} ${c2[0][1]} ${c2[1][0]} ${c2[1][1]} ${c2[2][0]} ${c2[2][1]}`);
    }

    pathParts.push("Z");
    return pathParts.join(" ");
  }
}
